import * as React from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Text, Flex, useIsMobileScreenSize } from '@buffer-mono/popcorn'

import { useKeyboardShortcut } from '~publish/hooks/useKeyboardShortcut'

import { CommentsForPost } from './CommentsForPost'
import { OpenPostsPanelButton } from './OpenPostsPanelButton'
import type { CommentsForPostChannel } from './CommentsForPost'
import { useAutoFetchUntilPostInList } from './hooks/useAutoFetchUntilPostInList'
import { useAutoSelectPost } from './hooks/useAutoSelectPost'
import { useClearSelectionOnChannelChange } from './hooks/useClearSelectionOnChannelChange'
import { usePostKeyboardNavigation } from './hooks/usePostKeyboardNavigation'
import { usePostsQuery } from './hooks/usePostsQuery'
import { usePostsWithCommentsToggle } from './hooks/usePostsWithCommentsToggle'
import { useReloadPostsToTop } from './hooks/useReloadPostsToTop'
import { useSelectedPostData } from './hooks/useSelectedPostData'
import { PostsSidepanel } from './PostsSidepanel'
import type { PostListItem } from './types'
import { buildPostRoute, buildPostsListRoute } from './utils'
import { CommentsByPostCountsProvider } from '../context/CommentsByPostCountsContext'
import { useCommentsTracking } from '../../hooks/tracking/useCommentsTracking'
import { setPostsPanelCollapsed } from '../../hooks/ui/postsPanelSignal'
import { COMMENT_STATUS } from '../../types'
import { isGoogleBusinessProfile } from '../../util/channelHelpers'
import { useFilters } from '../../CommunityFilters/useFilters'
import {
  ListeningForComments,
  NoCommentsFound,
} from '../CommentsList/EmptyStates'
import type { EmptyStateChannel } from '../CommentsList/EmptyStates/types'

import styles from './CommentsByPostView.module.css'

import type { JSX } from 'react'

/**
 * Union of every colocated channel fragment the by-post view threads through:
 * the selected post's comments feed ({@link CommentsForPostChannel}) and the
 * shared empty states ({@link EmptyStateChannel}). Both still carry codegen
 * intersections that collapse once their consumers migrate (CORE-6384).
 */
export type CommentsByPostViewChannel = CommentsForPostChannel &
  EmptyStateChannel

type CommentsByPostViewProps = {
  channels: CommentsByPostViewChannel[]
  /**
   * Optional callback used by empty states (e.g. NoCommentsFound on the
   * Replied / Resolved tabs) to jump back to the Unanswered status filter.
   */
  onNavigateToUnreplied?: () => void
  /**
   * Indicates if this is the all-channels view context.
   * When true, navigation stays on all-channels routes even if filtered to a single channel.
   */
  isAllChannelsView?: boolean
  /**
   * Optional notice element rendered at the top of the comments detail area.
   */
  notice?: React.ReactNode
  /**
   * Optional override for the comments detail area. When provided, this replaces
   * the normal post/comments rendering — used e.g. when the channel needs a
   * connection refresh so the posts sidepanel still renders but the main pane
   * shows the refresh empty state.
   */
  mainContent?: React.ReactNode
  /** When true, the posts sidepanel empty state uses the no-channels-connected copy. */
  noChannelsConnected?: boolean
}

export function CommentsByPostView({
  channels,
  onNavigateToUnreplied,
  isAllChannelsView = false,
  notice,
  mainContent,
  noChannelsConnected = false,
}: CommentsByPostViewProps): JSX.Element {
  const navigate = useNavigate()
  const { postId: selectedPostId } = useParams<{ postId?: string }>()
  const channelIds = React.useMemo(
    () => channels.map((channel) => channel.id),
    [channels],
  )

  const isMultiChannel = isAllChannelsView || channelIds.length > 1

  const isMobile = useIsMobileScreenSize()
  const [isPanelPinned, setIsPanelPinned] = React.useState(!isMobile)
  const containerRef = React.useRef<HTMLDivElement>(null)
  const openPanelButtonRef = React.useRef<HTMLButtonElement>(null)
  const pendingPanelFocus = React.useRef<'panel' | 'opener' | null>(null)

  const setPanelPinned = React.useCallback(
    (pinned: boolean) => {
      if (isMobile && pinned !== isPanelPinned) {
        pendingPanelFocus.current = pinned ? 'panel' : 'opener'
      }
      setIsPanelPinned(pinned)
    },
    [isMobile, isPanelPinned],
  )

  React.useEffect(() => {
    const target = pendingPanelFocus.current
    pendingPanelFocus.current = null
    if (target === 'opener') {
      openPanelButtonRef.current?.focus()
    } else if (target === 'panel') {
      const panel = containerRef.current?.querySelector('aside')
      const focusTarget =
        panel?.querySelector<HTMLElement>('[aria-selected="true"]') ??
        panel?.querySelector<HTMLElement>('button')
      focusTarget?.focus()
    }
  }, [isPanelPinned])

  // The pill that announces new comments renders in the page header, above this
  // view, and centres itself over the comments column. It therefore needs the
  // posts sidepanel's current width, which follows this state.
  React.useEffect(() => {
    setPostsPanelCollapsed(!isPanelPinned)
  }, [isPanelPinned])

  // Kept separate from the effect above so a change to the panel state does not
  // publish a transient `false` on its way to the new value. The flat list view
  // has no posts sidepanel, so it must not inherit this flag.
  React.useEffect(() => (): void => setPostsPanelCollapsed(false), [])

  const handlePostSelect = React.useCallback(
    (postId: string, options?: { replace?: boolean }) => {
      const route = buildPostRoute({
        postId,
        isMultiChannel,
        channelId: channels[0]?.id,
      })
      if (route) {
        navigate(route, options?.replace ? { replace: true } : undefined)
      }
    },
    [navigate, channels, isMultiChannel],
  )

  const clearSelectedPost = React.useCallback(() => {
    const route = buildPostsListRoute({
      isMultiChannel,
      channelId: channels[0]?.id,
    })
    if (route) {
      navigate(route)
    }
  }, [navigate, channels, isMultiChannel])

  useClearSelectionOnChannelChange({
    channelIds,
    onChannelSelectionChange: clearSelectedPost,
  })

  const { trackCommentsFiltered } = useCommentsTracking()

  const handleToggleTracking = React.useCallback(
    (value: boolean) => {
      trackCommentsFiltered({
        channels: channels.map((channel) => ({
          id: channel.id,
          service: channel.service,
        })),
        filterType: 'postsWithComments',
        filterValue: value ? 'on' : 'off',
      })
    },
    [trackCommentsFiltered, channels],
  )

  const { showOnlyPostsWithComments, togglePostsWithComments, showAllPosts } =
    usePostsWithCommentsToggle({
      onSelectionReset: clearSelectedPost,
      onToggle: handleToggleTracking,
    })

  const {
    resolvedPosts: posts,
    postIds,
    loading: loadingPosts,
    error,
    lastElementRef,
    fetchingMore,
    hasNextPage,
    fetchMore,
    refetch,
  } = usePostsQuery({
    channelIds,
    showOnlyWithComments: showOnlyPostsWithComments,
  })

  const handleTogglePanel = React.useCallback(() => {
    setPanelPinned(!isPanelPinned)
  }, [isPanelPinned, setPanelPinned])

  useKeyboardShortcut('mod+shift+p', handleTogglePanel)
  useAutoSelectPost({ posts, selectedPostId, onPostSelect: handlePostSelect })
  usePostKeyboardNavigation({
    posts,
    selectedPostId,
    onPostSelect: handlePostSelect,
  })

  const { selectedPost, selectedPostChannel } = useSelectedPostData({
    posts,
    selectedPostId,
    channels,
  })

  useAutoFetchUntilPostInList({
    selectedPostId,
    selectedPost,
    hasNextPage,
    fetchingMore,
    fetchMore,
  })

  const reloadPostsToTop = useReloadPostsToTop({
    posts,
    selectedPostId,
    onPostSelect: handlePostSelect,
    refetch,
  })

  const scrollContainerRef = React.useRef<HTMLDivElement | null>(null)

  const panelToggle =
    isMobile && !isPanelPinned ? (
      <OpenPostsPanelButton
        ref={openPanelButtonRef}
        onClick={() => setPanelPinned(true)}
      />
    ) : undefined
  const showsPostNavigation =
    !mainContent &&
    Boolean(selectedPostId && selectedPost && selectedPostChannel)

  // When the parent has supplied an override for the main content (e.g. an
  // unsupported-channel or needs-refresh empty state), render that and ignore
  // the posts query error — the query is expected to fail for those channels.
  if (error && !mainContent) {
    return <PostsListError />
  }

  return (
    <CommentsByPostCountsProvider
      postIds={postIds}
      everyPostHasComments={showOnlyPostsWithComments}
    >
      <div ref={containerRef} className={styles.container}>
        <PostsSidepanel
          posts={posts}
          selectedPostId={selectedPostId}
          onPostSelect={handlePostSelect}
          lastElementRef={lastElementRef}
          fetchingMore={fetchingMore}
          loading={loadingPosts}
          channels={channels}
          isMultiChannel={isMultiChannel}
          isPinned={isPanelPinned}
          onPinnedChange={setPanelPinned}
          showOnlyPostsWithComments={showOnlyPostsWithComments}
          onShowOnlyPostsWithCommentsChange={togglePostsWithComments}
          onShowAllPosts={showAllPosts}
          onRefreshPosts={reloadPostsToTop}
          noChannelsConnected={noChannelsConnected}
        />

        <div
          ref={scrollContainerRef}
          className={styles.commentsDetail}
          data-comments-detail=""
        >
          {notice}
          {panelToggle && !showsPostNavigation && (
            <div className={styles.panelBar}>{panelToggle}</div>
          )}
          {mainContent ?? (
            <CommentsDetailContent
              selectedPostId={selectedPostId}
              selectedPost={selectedPost}
              selectedPostChannel={selectedPostChannel}
              channels={channels}
              onNavigateToUnreplied={onNavigateToUnreplied}
              isFetchingMoreToFindPost={fetchingMore}
              posts={posts}
              onPostSelect={handlePostSelect}
              isPanelPinned={isPanelPinned}
              scrollContainerRef={scrollContainerRef}
              hasNoPosts={!loadingPosts && posts.length === 0}
              panelToggle={panelToggle}
            />
          )}
        </div>
      </div>
    </CommentsByPostCountsProvider>
  )
}

type CommentsDetailContentProps = {
  selectedPostId?: string
  selectedPost?: PostListItem
  selectedPostChannel?: CommentsByPostViewChannel
  channels: CommentsByPostViewChannel[]
  onNavigateToUnreplied?: () => void
  isFetchingMoreToFindPost?: boolean
  posts: PostListItem[]
  onPostSelect: (postId: string) => void
  isPanelPinned: boolean
  scrollContainerRef: React.RefObject<HTMLDivElement | null>
  hasNoPosts?: boolean
  panelToggle?: React.ReactNode
}

function CommentsDetailContent({
  selectedPostId,
  selectedPost,
  selectedPostChannel,
  channels,
  onNavigateToUnreplied,
  isFetchingMoreToFindPost = false,
  posts,
  onPostSelect,
  isPanelPinned,
  scrollContainerRef,
  hasNoPosts = false,
  panelToggle,
}: CommentsDetailContentProps): JSX.Element {
  const {
    filters,
    utils: { clearFilters, hasActiveFilters },
  } = useFilters()

  if (selectedPostId && selectedPost && selectedPostChannel) {
    return (
      <CommentsForPost
        key={selectedPostId}
        post={selectedPost.raw}
        channel={selectedPostChannel}
        cleanupChannels={channels}
        posts={posts}
        selectedPostId={selectedPostId}
        onPostSelect={onPostSelect}
        isPanelPinned={isPanelPinned}
        scrollContainerRef={scrollContainerRef}
        panelToggle={panelToggle}
      />
    )
  }

  if (selectedPostId && selectedPost && !selectedPostChannel) {
    return <ChannelNotAvailableMessage />
  }

  if (selectedPostId && !selectedPost) {
    return <PostNotInListYetMessage isFetchingMore={isFetchingMoreToFindPost} />
  }

  if (hasNoPosts) {
    const status = filters.status ?? COMMENT_STATUS.All
    // Mirror the List view's `useEmptyStateRenderer` decision: for a channel
    // that has never received comments, the All and Unanswered tabs land on
    // ListeningForComments (no filters active), while Replied / Resolved and
    // any custom-filter combo show NoCommentsFound.
    const showListening =
      !hasActiveFilters &&
      (status === COMMENT_STATUS.All || status === COMMENT_STATUS.Unreplied)

    return (
      <Flex className={styles.noPostSelected}>
        {showListening ? (
          <ListeningForComments isGBP={isGoogleBusinessProfile(channels)} />
        ) : (
          <NoCommentsFound
            status={status}
            hasActiveFilters={hasActiveFilters}
            onClearFilters={clearFilters}
            onNavigateToUnreplied={onNavigateToUnreplied}
            channels={channels}
          />
        )}
      </Flex>
    )
  }

  return <NoPostSelectedMessage />
}

function ChannelNotAvailableMessage(): JSX.Element {
  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      gap="sm"
      className={styles.noPostSelected}
    >
      <Text size="lg" weight="medium">
        Channel not available
      </Text>
      <Text color="subtle" align="center">
        The channel for this post is not available or has been filtered out.
      </Text>
    </Flex>
  )
}

function PostNotInListYetMessage({
  isFetchingMore = false,
}: {
  isFetchingMore?: boolean
}): JSX.Element {
  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      gap="sm"
      className={styles.noPostSelected}
    >
      <Text size="lg" weight="medium">
        {isFetchingMore ? 'Loading more posts…' : 'Loading post'}
      </Text>
      <Text color="subtle" align="center">
        {isFetchingMore
          ? 'Fetching more posts to find this one.'
          : 'Scroll the posts list on the left to load more. This post will appear when you reach it.'}
      </Text>
    </Flex>
  )
}

function NoPostSelectedMessage(): JSX.Element {
  return (
    <Flex className={styles.noPostSelected}>
      <Text color="subtle" align="center">
        Select a post to view its comments
      </Text>
    </Flex>
  )
}

function PostsListError(): JSX.Element {
  return (
    <div className={styles.container}>
      <Flex
        direction="column"
        align="center"
        justify="center"
        gap="sm"
        className={styles.noPostSelected}
      >
        <Text size="lg" weight="medium">
          Error loading posts
        </Text>
        <Text color="subtle" align="center">
          Unable to load posts. Please try refreshing the page.
        </Text>
      </Flex>
    </div>
  )
}
